import React, { useState, useEffect, useRef } from "react";

export default function HomeQuestions() {
  const [headerPosition, setHeaderPosition] = useState("normal");
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const questionsRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initialize on mount

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (
        !containerRef.current ||
        !headerRef.current ||
        !questionsRef.current ||
        isMobile
      )
        return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const headerRect = headerRef.current.getBoundingClientRect();
      const questionsRect = questionsRef.current.getBoundingClientRect();

      const containerTop = containerRect.top;
      const headerHeight = headerRect.height;
      const questionsBottom = questionsRect.bottom;

      // When container top reaches top of viewport (start of sticky behavior)
      const shouldStartSticky = containerTop <= 0;

      // When questions section bottom would go above viewport (end of sticky behavior)
      const shouldEndSticky = questionsBottom <= headerHeight;

      if (!shouldStartSticky) {
        // Normal position - header follows natural document flow
        setHeaderPosition("normal");
      } else if (shouldEndSticky) {
        // Fixed at bottom - header stops moving when questions end
        setHeaderPosition("bottom");
      } else {
        // Sticky at top - header sticks to top of viewport
        setHeaderPosition("fixed");
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial call

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  const QuestionAnswer = ({ question, answer, index }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "80px",
      }}
    >
      <h3 className="title-cool" style={{ fontSize: "2.5rem" }}>
        {index}
      </h3>

      <div>
        <h3
          className="title-cool"
          style={{ textAlign: "left", fontSize: "2.5rem" }}
        >
          {question}
        </h3>
        <p
          style={{
            fontSize: "1.1rem",
            color: "#6B6B6B",
            lineHeight: "1.5rem",
            maxWidth: "95%",
          }}
        >
          {answer}
        </p>
      </div>
    </div>
  );

  const Divider = () => <hr color="#F8F8F8" style={{ margin: "40px 0px" }} />;

  const getHeaderStyles = () => {
    const baseStyles = {
      maxWidth: isMobile ? "100%" : "600px",
      transition: "all 0.3s ease",
      display: isMobile && "flex",
      flexDirection: isMobile && "column",
      alignItems: isMobile ? "center" : "flex-start",
      marginBottom: isMobile ? "60px" : "0",
    };

    if (isMobile) {
      return baseStyles;
    }

    switch (headerPosition) {
      case "fixed":
        return {
          ...baseStyles,
          position: "sticky",
          top: "80px",
          zIndex: 1000,
        };
      case "bottom":
        return {
          ...baseStyles,
          position: "sticky",
          top: "80px",
        };
      default:
        return baseStyles;
    }
  };

  return (
    <div
      id="how-it-works"
      className="bg-circle-center-small"
      ref={containerRef}
      style={{
        alignItems: "flex-start",
        padding: "150px 32px",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "center" : "flex-start",
          width: "100%",
          maxWidth: "1400px",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {/* Header Section */}
        <div ref={headerRef} style={getHeaderStyles()}>
          <div
            style={{
              background: "linear-gradient(180deg, #051C0E 0%, #103A1B 100%)",
              borderRadius: "32px",
              border: "1px solid white",
              padding: "8px 24px",
              fontSize: "14px",
              fontWeight: "500",
              color: "white",
              width: "fit-content",
              letterSpacing: "0.1em",
              marginBottom: "20px",
            }}
          >
            HOW IT WORKS
          </div>
          <h1
            className="title-cool"
            style={{
              fontSize: "2.5rem",
              textAlign: isMobile ? "center" : "start",
              margin: "0",
            }}
          >
            Turn Market Chaos into a Clear Command Center
          </h1>
        </div>

        {/* Spacer for sticky header - only show in desktop mode */}
        {!isMobile && headerPosition === "sticky" && (
          <div style={{ maxWidth: "600px", height: "200px" }} />
        )}

        {/* Questions Section */}
        <div
          ref={questionsRef}
          style={{
            textAlign: "start",
            maxWidth: isMobile ? "100%" : "650px",
            flex: 1,
            marginLeft: isMobile
              ? "0"
              : headerPosition === "sticky"
                ? "0"
                : "auto",
          }}
        >
          <QuestionAnswer
            question={"Decode"}
            answer={
              "We transform dense COT reports into visuals that reveal market direction in seconds."
            }
            index={"01"}
          />
          <Divider />

          <QuestionAnswer
            question={"Filter"}
            answer={
              "AI pinpoints specific shifts and liquidations affecting your inventory."
            }
            index={"02"}
          />

          <Divider />
          <QuestionAnswer
            question={"Anticipate"}
            answer={
              "Identify momentum changes early to adjust your plan before the window closes."
            }
            index={"03"}
          />

          <Divider />
          <QuestionAnswer
            question={"Execute"}
            answer={
              "Move with confidence using data-backed logic to protect your operation."
            }
            index={"04"}
          />
        </div>
      </div>
    </div>
  );
}
